import IdleTimer from '@/components/IdleTimer';
import NavigationConfigListener from '@/components/NavigationConfigListener';
import { applyConfigToRoutes } from '@/configs/routesConfigUtils';
import routes from '@/configs/routesConfig';
import AppContext from '@/contexts/AppContext';
import store from '@/store/store';
import Authentication from '@auth/Authentication';
import FuseLayout from '@fuse/core/FuseLayout';
import FuseSettingsProvider from '@fuse/core/FuseSettings/FuseSettingsProvider';
import ErrorBoundary from '@fuse/utils/ErrorBoundary';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { enUS } from 'date-fns/locale/en-US';
import { useEffect, useMemo, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { Provider } from 'react-redux';
import themeLayouts from 'src/components/theme-layouts/themeLayouts';
import { ConfigValues, getLocalConfig } from '@/utils/configManager';
import MainThemeProvider from '../contexts/MainThemeProvider';
import './instrument';
import { CommandPaletteProvider } from '@/components/command';

/**
 * The main App component.
 */
function App() {
	const [configuredRoutes, setConfiguredRoutes] = useState(routes);

	// Apply route configurations
	useEffect(() => {
		try {
			// Load all available configs from localStorage
			const configs: Record<string, ConfigValues> = {};
			const configNames = [
				'account_config',
				'mis_config',
				'billing_config',
				'webrar_config',
				'zone_editor_config',
				'onboarding_config',
				'pricelist_config',
				'navbar_config'
			];

			for (const configName of configNames) {
				const config = getLocalConfig(configName);

				if (config && Object.keys(config).length > 0) {
					configs[configName] = config;
				}
			}

			// Apply configurations to routes
			const newRoutes = applyConfigToRoutes(routes, configs);
			setConfiguredRoutes(newRoutes);
		} catch (error) {
			console.warn('Failed to apply route configurations:', error);
			setConfiguredRoutes(routes);
		}
	}, []);

	// Listen for config updates
	useEffect(() => {
		const handleConfigUpdate = () => {
			try {
				const configs: Record<string, ConfigValues> = {};
				const configNames = [
					'account_config',
					'mis_config',
					'billing_config',
					'webrar_config',
					'zone_editor_config',
					'onboarding_config',
					'pricelist_config',
					'navbar_config'
				];

				for (const configName of configNames) {
					const config = getLocalConfig(configName);

					if (config && Object.keys(config).length > 0) {
						configs[configName] = config;
					}
				}

				const newRoutes = applyConfigToRoutes(routes, configs);
				setConfiguredRoutes(newRoutes);
			} catch (error) {
				console.warn('Failed to apply route configurations:', error);
			}
		};

		const handleStorageChange = () => {
			handleConfigUpdate();
		};

		window.addEventListener('configUpdated', handleConfigUpdate as EventListener);
		window.addEventListener('storage', handleStorageChange);

		return () => {
			window.removeEventListener('configUpdated', handleConfigUpdate as EventListener);
			window.removeEventListener('storage', handleStorageChange);
		};
	}, []);

	const AppContextValue = useMemo(() => ({
		routes: configuredRoutes
	}), [configuredRoutes]);

	return (
		<ErrorBoundary>
			<AppContext value={AppContextValue}>
				<LocalizationProvider
					dateAdapter={AdapterDateFns}
					adapterLocale={enUS}
				>
					<Provider store={store}>
						<Authentication>
							<NavigationConfigListener />
							<FuseSettingsProvider>
								<MainThemeProvider>
									<Toaster
										position='top-right'
										toastOptions={{
											duration: 5000,
											removeDelay: 1000,
										}}
										containerStyle={{
											'zIndex': 10000,
										}}
									/>
									<IdleTimer />
									<CommandPaletteProvider>
										<FuseLayout layouts={themeLayouts} />
									</CommandPaletteProvider>
								</MainThemeProvider>
							</FuseSettingsProvider>
						</Authentication>
					</Provider>
				</LocalizationProvider>
			</AppContext>
		</ErrorBoundary>
	);
}

export default App;
